import React, { useState } from 'react';
import {
  AdminContentContainer,
  AdminContentTitle,
  AdminContentSearchContainer,
  DateInput,
  SearchButton,
  BackButton,
  BookingDetailsContainer,
  Detail,
  GuestList,
  GuestItem,
  NoResults,
  BookingListContainer,
  BookingListItem,
  Subtitle,
  CancelButton,
  RefundOptionsContainer,
  RefundOption,
  ConfirmCancel,
} from '../styles';
import { adminGetBookingsPerDate, adminCancelBooking } from '../api';
import { Spinner } from ".";

const BookingDetailsWithSelection = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isRefundEnabled, setIsRefundEnabled] = useState(null);
  const [isRefundVisible, setIsRefundVisible] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isNoBookingsFound, setIsNoBookingsFound] = useState(false);

  const formatDateForSearch = (date) => {
    return date; // já vem no formato yyyy-mm-dd
  };

  const formatDateForDisplay = (date) => {
    return date.split('-').reverse().join('/');
  };

  const handleSearch = async () => {
    if (!selectedDate) return;

    setIsLoading(true);
    setIsNoBookingsFound(false);
    setError(null);

    try {

      const formattedDate = formatDateForSearch(selectedDate);

      const response = await adminGetBookingsPerDate(formattedDate);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`Erro ao buscar marcações: ${response.statusText}`);
      }

      setBookings(data[0][formattedDate]);

      if (bookings.length > 0) {
        setIsNoBookingsFound(false);

      } else {
        setIsNoBookingsFound(true);
      }

      setSelectedBooking(null);

    } catch (error) {
      console.error('Erro ao buscar marcações:', error);
      setError('Erro ao carregar marcações.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookingSelection = (id) => {
    const booking = bookings.find((b) => b.id === id);
    setSelectedBooking(booking);
  };

  const handleCancelBooking = async () => {
    setIsRefundVisible(true);
  };

  const handleRefundChange = (value) => {
    setIsRefundEnabled(value);
  };

  const handleBack = () => {
    setSelectedBooking(null);
  };

  const handleConfirmCancel = async () => {
    if (selectedBooking && isRefundEnabled !== null) {
      try {

        const response = await adminCancelBooking(selectedBooking.id, isRefundEnabled);

        const result = await response.json();

        if (response.ok) {
          const updatedBookings = bookings.filter(
            (b) => b.id !== selectedBooking.id
          );
          setBookings(updatedBookings);
          setSelectedBooking(null);
          setIsRefundVisible(false);

          setError(result.message || 'Reserva cancelada com sucesso.');

          if (updatedBookings.length === 0) {
            setIsNoBookingsFound(true);
          }

        } else {
          console.error('Erro ao cancelar reserva');
          setError(result.message || 'Erro ao cancelar reserva.');
        }

      } catch (error) {
        console.error('Erro ao cancelar reserva:', error);
        setError('Erro ao cancelar reserva.');
      }

    } else {
      console.error('Seleção de reembolso não foi feita.');
      setError('Selecione uma opção de reembolso para continuar.');
    }
  };

  const isCancelButtonDisabled = isRefundEnabled === null;

  return (
    <AdminContentContainer>
      <AdminContentTitle>Visualizar Detalhes de Marcação</AdminContentTitle>

      <AdminContentSearchContainer>
        <DateInput
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
        <SearchButton onClick={handleSearch} disabled={!selectedDate}>
          Buscar
        </SearchButton>
      </AdminContentSearchContainer>

      {error && <NoResults>{error}</NoResults>}

      {isLoading ? <Spinner /> : (
        bookings.length > 0 ? (
          selectedBooking ? (
            <BookingDetailsContainer>
              <Subtitle>Detalhes da Marcação</Subtitle>
              {selectedBooking.date && (
                <Detail>
                  <strong>Data:</strong>{' '}
                  {formatDateForDisplay(selectedBooking.date)}
                </Detail>
              )}
              <Detail>
                <strong>Horário:</strong> {selectedBooking.start_time} -{' '}
                {selectedBooking.end_time}
              </Detail>
              <Detail>
                <strong>Nome completo:</strong> {selectedBooking.fullName}
              </Detail>
              <Detail>
                <strong>
                  {selectedBooking.invitedUsers?.length > 1
                    ? 'Convidados'
                    : 'Convidado'}
                  :
                </strong>
                <GuestList>
                  {selectedBooking.invitedUsers?.map((guest, index) => (
                    <GuestItem key={index}>{guest}</GuestItem>
                  ))}
                </GuestList>
              </Detail>
              <Detail>
                <strong>Status da reserva:</strong> {selectedBooking.status}
              </Detail>
              <Detail>
                <strong>Tipo da reserva:</strong>{' '}
                {selectedBooking.type === 'unique' ? 'Avulsa' : 'Mensal'}
              </Detail>

              <BackButton onClick={handleBack}>
                Voltar
              </BackButton>

              <CancelButton onClick={handleCancelBooking}>
                Cancelar reserva
              </CancelButton>

              {isRefundVisible && (
                <RefundOptionsContainer>
                  <RefundOption>
                    <input
                      type="radio"
                      id="refund-yes"
                      name="refund"
                      value="true"
                      checked={isRefundEnabled === true}
                      onChange={() => handleRefundChange(true)}
                    />
                    <label style={{ fontSize: '15px' }} htmlFor="refund-yes">
                      Habilitar reposição
                    </label>
                  </RefundOption>

                  <RefundOption>
                    <input
                      type="radio"
                      id="refund-no"
                      name="refund"
                      value="false"
                      checked={isRefundEnabled === false}
                      onChange={() => handleRefundChange(false)}
                    />
                    <label style={{ fontSize: '15px' }} htmlFor="refund-no">
                      Não habilitar reposição
                    </label>
                  </RefundOption>

                  <ConfirmCancel
                    onClick={handleConfirmCancel}
                    disabled={isCancelButtonDisabled}
                    className={isCancelButtonDisabled ? 'disabled' : ''}
                  >
                    Confirmar Cancelamento
                  </ConfirmCancel>
                </RefundOptionsContainer>
              )}
            </BookingDetailsContainer>
          ) : (
            <BookingListContainer>
              <Subtitle>Selecione uma marcação:</Subtitle>
              {bookings.map((booking) => (
                <BookingListItem
                  key={booking.id}
                  onClick={() => handleBookingSelection(booking.id)}
                >
                  <span>
                    {booking.start_time} - {booking.end_time}
                  </span>
                  <span>{booking.fullName}</span>
                </BookingListItem>
              ))}
            </BookingListContainer>
          )
        ) : (
          isNoBookingsFound && (
            <NoResults>
              Nenhuma marcação encontrada para{' '}
              {formatDateForDisplay(selectedDate)}
            </NoResults>
          )
        )
      )}
    </AdminContentContainer>
  );
};

export default BookingDetailsWithSelection;
